import React, { useEffect, useState } from 'react';
import api from '../api/api';

const initialForm = {
    platform: 'android',
    versionNumber: '',
    minimumVersion: '',
    isForceUpdate: false,
    changelogAr: '',
    changelogEn: '',
    downloadUrl: '',
    sendPushNotification: false,
};

function AppVersions() {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [versions, setVersions] = useState([]);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [filterPlatform, setFilterPlatform] = useState('');

    useEffect(() => {
        fetchVersions();
    }, []);

    const fetchVersions = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin-app-versions');
            const data = Array.isArray(response.data) ? response.data : [];
            setVersions(data);
        } catch (error) {
            console.error('Error loading versions:', error);
            alert('فشل تحميل الإصدارات');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.versionNumber.trim() || !form.minimumVersion.trim()) {
            alert('يرجى ملء رقم الإصدار والحد الأدنى للإصدار');
            return;
        }
        try {
            setSaving(true);
            if (editId) {
                await api.put(`/admin-app-versions/${editId}`, {
                    minimumVersion: form.minimumVersion,
                    isForceUpdate: form.isForceUpdate,
                    changelogAr: form.changelogAr,
                    changelogEn: form.changelogEn,
                    downloadUrl: form.downloadUrl,
                    isActive: true,
                });
                alert('تم تحديث الإصدار بنجاح');
            } else {
                await api.post('/admin-app-versions', form);
                alert(form.sendPushNotification
                    ? 'تم إنشاء الإصدار وإرسال الإشعار بنجاح'
                    : 'تم إنشاء الإصدار بنجاح');
            }
            setForm(initialForm);
            setEditId(null);
            fetchVersions();
        } catch (error) {
            console.error('Error saving version:', error);
            alert('فشل حفظ الإصدار');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (v) => {
        setEditId(v.id);
        setForm({
            platform: v.platform,
            versionNumber: v.versionNumber,
            minimumVersion: v.minimumVersion,
            isForceUpdate: v.isForceUpdate,
            changelogAr: v.changelogAr || '',
            changelogEn: v.changelogEn || '',
            downloadUrl: v.downloadUrl || '',
            sendPushNotification: false,
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('هل أنت متأكد من حذف هذا الإصدار؟')) return;
        try {
            await api.delete(`/admin-app-versions/${id}`);
            alert('تم حذف الإصدار بنجاح');
            fetchVersions();
        } catch (error) {
            console.error('Error deleting version:', error);
            alert('فشل حذف الإصدار');
        }
    };

    const handleToggleActive = async (v) => {
        try {
            await api.put(`/admin-app-versions/${v.id}`, { isActive: !v.isActive });
            fetchVersions();
        } catch (error) {
            console.error('Error toggling version:', error);
        }
    };

    const filteredVersions = filterPlatform
        ? versions.filter(v => v.platform === filterPlatform)
        : versions;

    return (
        <div className="container-fluid" style={{ direction: 'rtl' }}>
            <h3 className="mb-4">📱 إدارة إصدارات التطبيق</h3>

            {/* Form */}
            <div className="card mb-4 shadow-sm">
                <div className="card-body">
                    <h5 className="card-title mb-3">
                        {editId ? '✏️ تعديل الإصدار' : '➕ إضافة إصدار جديد'}
                    </h5>
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            {/* Platform */}
                            <div className="col-md-4">
                                <label className="form-label">المنصة</label>
                                <select
                                    className="form-select"
                                    value={form.platform}
                                    onChange={e => setForm({ ...form, platform: e.target.value })}
                                    disabled={!!editId}
                                >
                                    <option value="android">Android</option>
                                    <option value="ios">iOS</option>
                                </select>
                            </div>

                            {/* Version Number */}
                            <div className="col-md-4">
                                <label className="form-label">رقم الإصدار</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="مثال: 1.2.0"
                                    value={form.versionNumber}
                                    onChange={e => setForm({ ...form, versionNumber: e.target.value })}
                                    disabled={!!editId}
                                    dir="ltr"
                                />
                            </div>

                            {/* Minimum Version */}
                            <div className="col-md-4">
                                <label className="form-label">الحد الأدنى للإصدار</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="مثال: 1.0.0"
                                    value={form.minimumVersion}
                                    onChange={e => setForm({ ...form, minimumVersion: e.target.value })}
                                    dir="ltr"
                                />
                            </div>

                            {/* Download URL */}
                            <div className="col-md-12">
                                <label className="form-label">رابط التحميل</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="رابط Google Play أو App Store"
                                    value={form.downloadUrl}
                                    onChange={e => setForm({ ...form, downloadUrl: e.target.value })}
                                    dir="ltr"
                                />
                            </div>

                            {/* Changelog AR */}
                            <div className="col-md-6">
                                <label className="form-label">سجل التغييرات (عربي)</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="ما الجديد في هذا الإصدار..."
                                    value={form.changelogAr}
                                    onChange={e => setForm({ ...form, changelogAr: e.target.value })}
                                />
                            </div>

                            {/* Changelog EN */}
                            <div className="col-md-6">
                                <label className="form-label">سجل التغييرات (إنجليزي)</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="What's new in this version..."
                                    value={form.changelogEn}
                                    onChange={e => setForm({ ...form, changelogEn: e.target.value })}
                                    dir="ltr"
                                />
                            </div>

                            {/* Checkboxes */}
                            <div className="col-md-6">
                                <div className="form-check mt-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="forceUpdate"
                                        checked={form.isForceUpdate}
                                        onChange={e => setForm({ ...form, isForceUpdate: e.target.checked })}
                                    />
                                    <label className="form-check-label" htmlFor="forceUpdate">
                                        ⚠️ تحديث إجباري (يمنع المستخدم من استخدام التطبيق)
                                    </label>
                                </div>
                            </div>

                            {!editId && (
                                <div className="col-md-6">
                                    <div className="form-check mt-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="sendPush"
                                            checked={form.sendPushNotification}
                                            onChange={e => setForm({ ...form, sendPushNotification: e.target.checked })}
                                        />
                                        <label className="form-check-label" htmlFor="sendPush">
                                            🔔 إرسال إشعار لجميع المستخدمين
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-3 d-flex gap-2">
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? '⏳ جاري الحفظ...' : (editId ? '💾 تحديث' : '➕ إضافة')}
                            </button>
                            {editId && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => { setEditId(null); setForm(initialForm); }}
                                >
                                    ❌ إلغاء
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>

            {/* Filter */}
            <div className="d-flex align-items-center gap-3 mb-3">
                <label className="form-label mb-0">تصفية حسب المنصة:</label>
                <select
                    className="form-select"
                    style={{ width: '200px' }}
                    value={filterPlatform}
                    onChange={e => setFilterPlatform(e.target.value)}
                >
                    <option value="">الكل</option>
                    <option value="android">Android</option>
                    <option value="ios">iOS</option>
                </select>
            </div>

            {/* Versions Table */}
            {loading ? (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" />
                    <p className="mt-2">جاري التحميل...</p>
                </div>
            ) : filteredVersions.length === 0 ? (
                <div className="alert alert-info">لا توجد إصدارات مسجلة</div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover table-bordered align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>المنصة</th>
                                <th>رقم الإصدار</th>
                                <th>الحد الأدنى</th>
                                <th>إجباري</th>
                                <th>الحالة</th>
                                <th>التاريخ</th>
                                <th>الإجراءات</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVersions.map(v => (
                                <tr key={v.id}>
                                    <td>
                                        <span className={`badge ${v.platform === 'android' ? 'bg-success' : 'bg-dark'}`}>
                                            {v.platform === 'android' ? '🤖 Android' : '🍎 iOS'}
                                        </span>
                                    </td>
                                    <td dir="ltr"><strong>{v.versionNumber}</strong></td>
                                    <td dir="ltr">{v.minimumVersion}</td>
                                    <td>
                                        {v.isForceUpdate
                                            ? <span className="badge bg-danger">⚠️ إجباري</span>
                                            : <span className="badge bg-secondary">اختياري</span>}
                                    </td>
                                    <td>
                                        <span
                                            className={`badge ${v.isActive ? 'bg-success' : 'bg-warning text-dark'}`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleToggleActive(v)}
                                            title="اضغط لتبديل الحالة"
                                        >
                                            {v.isActive ? '✅ مفعل' : '⏸️ معطل'}
                                        </span>
                                    </td>
                                    <td style={{ fontSize: '0.85rem' }}>
                                        {new Date(v.createdAt).toLocaleDateString('ar-SY')}
                                    </td>
                                    <td>
                                        <div className="d-flex gap-1">
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => handleEdit(v)}
                                                title="تعديل"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => handleDelete(v.id)}
                                                title="حذف"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default AppVersions;
